import SharedDialog from '../../../../shared/shared-dialog';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Calendar, CalendarAclResource } from '../../../../../types';
import { UserKeys, updateCalendarAcls } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Public from '@mui/icons-material/Public';
import Lock from '@mui/icons-material/Lock';
import Domain from '@mui/icons-material/Domain';
interface ChangeVisibilityDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedVisibility:
    | 'default_public'
    | 'user'
    | 'group'
    | 'domain'
    | 'private';
  selectedRole: string;
  userEmail: string;
  selectedCalendar: Calendar;
  primaryDomain: string;
  aclId: string | undefined;
  changeLogRoles: string[];
  isAllUsers: boolean;
}

export default function ChangeVisibilityDialog({
  open,
  handleClose,
  selectedVisibility,
  selectedRole,
  userEmail,
  selectedCalendar,
  primaryDomain,
  aclId,
  changeLogRoles,
  isAllUsers,
}: ChangeVisibilityDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending: isSharing } = useMutation({
    mutationFn: (formData: CalendarAclResource[]) =>
      updateCalendarAcls(userEmail, selectedCalendar, formData),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Calendar share permissions updated successfully!',
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.CalendarAcls, userEmail, selectedCalendar.id],
      });
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue sharing this calendar. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        isAllUsers
          ? analytics.calendars.calendars.update_calendar_visibility
          : analytics.user_management.user_details.calendars
              .update_calendar_visibility,
        isAllUsers
          ? {
              module: ModuleList.modules.calendars.name,
              section: ModuleList.modules.calendars.sections.calendars,
            }
          : {
              module: ModuleList.modules.userManagement.name,
              section: ModuleList.modules.userManagement.sections.calendars,
            }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function mutateData() {
    const changes = [];

    const publicToDomain =
      selectedVisibility === 'default_public' &&
      getValues('selectedVisibility') === 'domain';
    const domainToPublic =
      selectedVisibility === 'domain' &&
      getValues('selectedVisibility') === 'default_public';

    // delete out old acl
    if (
      publicToDomain ||
      (domainToPublic && getValues('selectedVisibility') !== 'private')
    ) {
      changes.push({
        // good with being old id, since it's being deleted
        id: aclId,
        scope: {
          type: selectedVisibility as
            | 'default_public'
            | 'user'
            | 'group'
            | 'domain',
        },
        role: selectedRole as
          | 'freeBusyReader'
          | 'none'
          | 'reader'
          | 'writer'
          | 'owner',
        canActOn: false,
        toBeDeleted: true,
      });
    }

    // delete out old acl
    if (getValues('selectedVisibility') === 'private') {
      changes.push({
        id: aclId,
        scope: {
          type: selectedVisibility as
            | 'default_public'
            | 'user'
            | 'group'
            | 'domain',
        },
        role: selectedRole as
          | 'freeBusyReader'
          | 'none'
          | 'reader'
          | 'writer'
          | 'owner',
        canActOn: false,
        toBeDeleted: true,
      });
    } else {
      changes.push({
        // this needs to be new id
        id:
          getValues('selectedVisibility') === 'domain'
            ? `domain:${primaryDomain}`
            : 'default',
        scope: {
          type: getValues('selectedVisibility') as
            | 'default_public'
            | 'user'
            | 'group'
            | 'domain',
          value:
            getValues('selectedVisibility') === 'domain' ? primaryDomain : '',
        },
        role: getValues('role') as
          | 'freeBusyReader'
          | 'none'
          | 'reader'
          | 'writer'
          | 'owner',
        canActOn: false,
        toBeDeleted: false,
      });
    }
    return changes;
  }

  const onSubmit: SubmitHandler<{
    role: string;
    selectedVisibility: string;
  }> = () => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        CalendarRoles.EditCalendarVisibility.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      const changes = mutateData();
      mutate(changes);
    }
  };
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    formState: { isDirty },
  } = useForm({
    values: {
      selectedVisibility: selectedVisibility,
      role: selectedRole === '' ? 'freeBusyReader' : selectedRole,
    },
  });

  const { selectedVisibility: vis } = watch();

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        reset();
      }}
      title="Change Visibility"
      dialogContent={
        <Box component="form">
          <ListItemButton
            autoFocus
            dense
            selected={vis === 'default_public'}
            onClick={() =>
              setValue('selectedVisibility', 'default_public', {
                shouldDirty: true,
              })
            }
          >
            <ListItemAvatar>
              <Avatar>
                <Public />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary="Public"
              secondary="Anyone on the Internet can find and access. No sign-in required."
            />
          </ListItemButton>
          <ListItemButton
            dense
            selected={vis === 'private'}
            onClick={() =>
              setValue('selectedVisibility', 'private', {
                shouldDirty: true,
              })
            }
          >
            <ListItemAvatar>
              <Avatar>
                <Lock />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary="Private"
              secondary="Only people listed below can access"
            />
          </ListItemButton>
          <ListItemButton
            dense
            selected={vis === 'domain'}
            onClick={() =>
              setValue('selectedVisibility', 'domain', {
                shouldDirty: true,
              })
            }
          >
            <ListItemAvatar>
              <Avatar>
                <Domain />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary="Domain"
              secondary={`Anyone at ${primaryDomain} can find and access`}
            />
          </ListItemButton>

          {vis !== 'private' ? (
            <>
              <Divider sx={{ my: 2 }} />
              <Controller
                name={`role`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputProps={{
                      id: 'role',
                    }}
                    InputLabelProps={{
                      htmlFor: 'role',
                    }}
                    select
                    defaultValue="freeBusyReader"
                    label="Role"
                    size="small"
                    fullWidth
                  >
                    <MenuItem key="freeBusyReader" value="freeBusyReader">
                      Free/Busy Viewer
                    </MenuItem>
                    <MenuItem key="reader" value="reader">
                      Viewer
                    </MenuItem>
                  </TextField>
                )}
              />
            </>
          ) : (
            <Box sx={{ height: 73 }}></Box>
          )}
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              const changes = mutateData();
              mutate(changes);
            }}
            mutateLoading={isSharing}
          />
        </Box>
      }
      primaryButton={{
        title: 'Save Changes',
        action: handleSubmit(onSubmit),
        disabled: !isDirty,
        loading: isSharing,
      }}
    />
  );
}
